import React from 'react';
import {
  COUNTER_ACCENT,
  COUNTER_HIGHLIGHT_BG,
  SAME_VALUE_COLOR,
} from '../constants';

export const getCounterTimestamp = item => {
  const value = item?.updatedDate ?? item?.createdDate;
  const time = value ? new Date(value).getTime() : NaN;
  return Number.isNaN(time) ? 0 : time;
};

export const pickLatestCounter = (items, mitraId) => {
  const list = Array.isArray(items) ? items : [];
  const scoped = list.filter(item => {
    if (mitraId == null || mitraId === '') return true;
    if (item.procurementMitraId == null || item.procurementMitraId === '') {
      return true;
    }
    return String(item.procurementMitraId) === String(mitraId);
  });
  if (!scoped.length) return null;
  return scoped.reduce((latest, item) =>
    getCounterTimestamp(item) >= getCounterTimestamp(latest) ? item : latest
  );
};

export const isSameId = (left, right, leftEntity, rightEntity) => {
  const a = left ?? leftEntity?.id ?? leftEntity?.Id ?? null;
  const b = right ?? rightEntity?.id ?? rightEntity?.Id ?? null;
  const normalize = value =>
    value == null || value === '' ? null : String(value);
  const normalizedA = normalize(a);
  const normalizedB = normalize(b);
  if (normalizedA === null && normalizedB === null) return true;
  if (normalizedA === null || normalizedB === null) return false;
  return normalizedA === normalizedB;
};

// Sama dengan BE (StripHtmlToPlainText → collapse spasi → trim): tag blok/br
// = spasi, tag inline dibuang, entity di-decode.
const BLOCK_TAG =
  /<\/?(br|p|div|li|ul|ol|h[1-6]|tr|table|blockquote)\b[^>]*>/gi;
const toPlainText = value => {
  const html = String(value ?? '').replace(BLOCK_TAG, ' ');
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body?.textContent ?? '').replace(/\s+/g, ' ').trim();
};

export const isSameHtml = (left, right) =>
  toPlainText(left) === toPlainText(right);

export const getEntityDisplayName = entity => {
  if (entity == null || entity === '') return '';
  if (typeof entity === 'string') return entity.trim();
  return String(
    entity.name ?? entity.Name ?? entity.description ?? entity.Description ?? ''
  ).trim();
};

export const getPaymentCurrencyDisplayName = entity => {
  if (!entity) return '';
  const name =
    entity.currencyName ??
    entity.CurrencyName ??
    entity.name ??
    entity.Name ??
    '';
  const code = entity.currencyCode ?? entity.CurrencyCode ?? '';
  if (name && code) return `${name} (${code})`;
  return String(name || code || '').trim();
};

export const resolvePaymentSelectLabel = (entity, options, id) => {
  const fromEntity = getEntityDisplayName(entity);
  if (fromEntity) return fromEntity;
  if (!Array.isArray(options) || id == null || id === '') return '-';
  return options.find(item => String(item.value) === String(id))?.label || '-';
};

export const resolvePaymentCurrencyLabel = (entity, id, options) => {
  const fromCurrency = getPaymentCurrencyDisplayName(entity);
  if (fromCurrency) return fromCurrency;
  return resolvePaymentSelectLabel(entity, options, id);
};

export const mergeSelectOptions = (...optionGroups) => {
  const merged = [];
  const seen = new Set();
  optionGroups.flat().forEach(option => {
    if (!option?.value) return;
    const key = String(option.value);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(option);
  });
  return merged;
};

export const CounterDetailItem = ({
  label,
  value,
  isHtml = false,
  unchanged = false,
}) => {
  const color = unchanged ? SAME_VALUE_COLOR : COUNTER_ACCENT;
  return (
    <div className='ps-3' style={{ borderLeft: `2px solid ${color}` }}>
      <div className='small fw-semibold mb-1' style={{ color }}>
        {label}
      </div>
      {isHtml ? (
        value ? (
          <div className='html-content-panel'>
            <div
              className='html-content-body'
              dangerouslySetInnerHTML={{ __html: value }}
            />
          </div>
        ) : (
          <div className='fs-13 mb-0 lh-base fw-semibold' style={{ color }}>
            -
          </div>
        )
      ) : (
        <div className='fs-13 mb-0 lh-base fw-semibold' style={{ color }}>
          {value || '-'}
        </div>
      )}
    </div>
  );
};

export const CounterValueBlock = ({ title, children }) => (
  <div
    className='p-3 rounded-3 mt-2'
    style={{
      borderLeft: `3px solid ${COUNTER_ACCENT}`,
      backgroundColor: COUNTER_HIGHLIGHT_BG,
    }}
  >
    <div className='fw-bold fs-13 mb-3' style={{ color: COUNTER_ACCENT }}>
      {title}
    </div>
    {children}
  </div>
);
